import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const ScheduleList = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();
  const { user } = useAuth();

  const loadSchedules = async () => {
    try {
      const res = await axios.get('/schedules');
      setSchedules(res.data || []);
    } catch {
      toast.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSchedules(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule? This will also remove all its shifts, mappings, remuneration and reports.')) return;
    setDeletingId(id);
    try {
      await axios.delete(`/schedules/${id}`);
      toast.success('Schedule deleted successfully');
      await loadSchedules(); // re-fetch from DB so dashboard and list stay in sync
    } catch (err) {
      const msg = err?.response?.data?.message || 'Delete failed';
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatus = async (id, status) => {
    try {
      await axios.put(`/schedules/${id}`, { status });
      toast.success('Status updated');
      setSchedules(prev =>
        prev.map(s => s.id === id ? { ...s, status } : s)
      );
    } catch (err) {
      console.error(err);
      toast.error('Update failed');
    }
  };

  const filtered = schedules.filter(
    (s) =>
      s.exam_name?.toLowerCase().includes(search.toLowerCase()) &&
      (statusFilter ? s.status === statusFilter : true)
  );

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const statusBadge = (status) => {
    const map = {
      active:    ['#fff3e0', '#f1870e'],
      completed: ['#e3f2fd', '#1565c0'],
      cancelled: ['#ffebee', '#c62828']
    };
    const [bg, color] = map[status] || ['#f5f5f5', '#666'];
    return (
      <span style={{
        backgroundColor: bg, color,
        padding: '3px 10px', borderRadius: '12px',
        fontSize: '12px', fontWeight: 600
      }}>
        {status}
      </span>
    );
  };

  const canEdit = ['super_admin', 'system_admin'].includes(user?.role);

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center',
        alignItems: 'center', height: '60vh',
        color: '#666', fontSize: '18px'
      }}>
        Loading schedules...
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', backgroundColor: '#f5f6fa', minHeight: '100vh' }}>

      {/* HEADER */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '24px'
      }}>
        <div>
          <h2 style={{ color: '#f1870e', margin: 0, fontSize: '26px', fontWeight: 700 }}>
            Schedules
          </h2>
          <p style={{ color: '#888', margin: '4px 0 0', fontSize: '14px' }}>
            {schedules.length} total schedules
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => navigate('/schedules/add')}
            style={{
              backgroundColor: '#f1870e', color: 'white',
              border: 'none', padding: '10px 20px',
              borderRadius: '8px', cursor: 'pointer',
              fontWeight: 600, fontSize: '14px'
            }}
          >
            + Add Schedule
          </button>
        )}
      </div>

      {/* FILTER */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input
          placeholder="Search exam name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1, minWidth: '220px', padding: '10px 14px',
            border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px'
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '10px 14px', border: '1px solid #ddd',
            borderRadius: '8px', fontSize: '14px', backgroundColor: 'white'
          }}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* TABLE */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden'
      }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '60px 0' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📅</div>
            No schedules found.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f1870e' }}>
                {['#', 'Exam Name', 'Type', 'Start Date', 'End Date', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '13px 14px', textAlign: 'left',
                    fontSize: '12px', color: 'white',
                    fontWeight: 700, textTransform: 'uppercase'
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => (
                <tr key={s.id} style={{
                  borderBottom: '1px solid #f0f0f0',
                  background: i % 2 === 0 ? '#fff' : '#fafafa'
                }}>
                  <td style={{ padding: '13px 14px', color: '#888' }}>{i + 1}</td>

                  <td
                    style={{ padding: '13px 14px', cursor: 'pointer', color: '#f1870e', fontWeight: 600 }}
                    onClick={() => navigate(`/schedules/${s.id}`)}
                  >
                    {s.exam_name}
                  </td>

                  <td style={{ padding: '13px 14px' }}>{s.exam_type || '—'}</td>
                  <td style={{ padding: '13px 14px' }}>{formatDate(s.start_date)}</td>
                  <td style={{ padding: '13px 14px' }}>{formatDate(s.end_date)}</td>
                  <td style={{ padding: '13px 14px' }}>{statusBadge(s.status)}</td>

                  <td style={{ padding: '13px 14px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => navigate(`/schedules/${s.id}`)}
                        style={{
                          backgroundColor: '#fff3e0', color: '#f1870e',
                          border: 'none', padding: '5px 12px',
                          borderRadius: '6px', cursor: 'pointer',
                          fontSize: '12px', fontWeight: 600
                        }}
                      >
                        View
                      </button>

                      {canEdit && s.status === 'active' && (
                        <button
                          onClick={() => handleStatus(s.id, 'completed')}
                          style={{
                            backgroundColor: '#e3f2fd', color: '#1565c0',
                            border: 'none', padding: '5px 12px',
                            borderRadius: '6px', cursor: 'pointer',
                            fontSize: '12px', fontWeight: 600
                          }}
                        >
                          Complete
                        </button>
                      )}

                      {canEdit && (
                        <button
                          onClick={() => handleDelete(s.id)}
                          disabled={deletingId === s.id}
                          style={{
                            backgroundColor: deletingId === s.id ? '#eee' : '#ffebee',
                            color: deletingId === s.id ? '#aaa' : '#c62828',
                            border: 'none', padding: '5px 12px',
                            borderRadius: '6px', cursor: deletingId === s.id ? 'not-allowed' : 'pointer',
                            fontSize: '12px', fontWeight: 600
                          }}
                        >
                          {deletingId === s.id ? 'Deleting…' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ScheduleList;